import { useQuery, useMutation, QueryClient, useQueryClient } from 'react-query'

import { useAuthedFetch } from '../auth/useAuthedFetch'
import withFetchCancel from '../libs/withFetchCancel'
import { useUserContext } from '../auth/UserProvider'
import { useCallback, useRef } from 'react'

const BACKEND_ENDPOINT = process.env.REACT_APP_BACKEND_ENDPOINT

const transformAddress = addressData => ({
  rawData: addressData,
  data: {
    id: addressData.client_address_code,
    name: addressData.name,
    address: addressData.address,
    country: addressData.country_code,
    postalCode: addressData.postal_code,
    city: addressData.address_2,
    province: addressData.state_or_province ?? '',
    email: addressData.email ?? '',
    phone: addressData.phone ?? '',
    contactClientCode: addressData.contact_client_code ?? '',
    comments: addressData.comments ?? '',
  },
})

const transformAddressBookResponse = respData => {
  const { data: AddressBookResponse, pagination: paginationResponse } = respData

  const data = AddressBookResponse.map(transformAddress)

  const pagination = {
    totalCount: paginationResponse?.record_count ?? 0,
    itemsPerPage: paginationResponse?.page_limit ?? null,
    currentPage: paginationResponse?.page_offsets?.current ?? null,
    nextPage: paginationResponse?.page_offsets?.next ?? null,
    lastPage: paginationResponse?.page_offsets?.last ?? null,
  }

  return {
    data,
    pagination,
  }
}

export const useAddressBookByCenter = (
  { centreCode, pageLimit, pageOffset, sortColumn, sortType },
  config,
) => {
  const authedFetch = useAuthedFetch()
  const orderBy = formatOrderBy(sortColumn, sortType)

  const query = useQuery(
    ['addresses', centreCode, pageLimit, pageOffset, sortColumn, sortType],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'GET',
          `${BACKEND_ENDPOINT}/client-centers/${centreCode}/client-addresses?page_limit=${pageLimit}&page_offsets=${pageOffset}${orderBy}`,
          {
            parseJSON: true,
            signal,
          },
        ),
      ),
    {
      select: ({ data }) => transformAddressBookResponse(data),
      enabled: Boolean(centreCode),
      useErrorBoundary: false,
      ...config,
    },
  )

  return query
}

export const useDeleteAddressMutation = (onDeleteSuccess, onDeleteError) => {
  const authedFetch = useAuthedFetch()

  return useMutation(
    ({ clientCenterCode, clientAddressCode }) =>
      withFetchCancel(signal =>
        authedFetch(
          'DELETE',
          `${BACKEND_ENDPOINT}/client-centers/${clientCenterCode}/client-addresses/${clientAddressCode}`,
          {
            signal,
            data: { clientCenterCode, clientAddressCode },
          },
        ),
      ),
    {
      useErrorBoundary: false,
      onSuccess: () => onDeleteSuccess(),
      onError: () => onDeleteError()
    },
  )
}

export const useDeleteAddressesMutation = (onDeleteSuccess, onDeleteError) => {
  const authedFetch = useAuthedFetch()

  return useMutation(
    ({ clientCenterCode, clientAddressesCode }) =>
      withFetchCancel(signal =>
        authedFetch(
          'DELETE',
          `${BACKEND_ENDPOINT}/client-centers/${clientCenterCode}/client-addresses`,
          {
            signal,
            data: {
              client_address_codes: clientAddressesCode.map(obj => obj.id),
            },
          },
        ),
      ),
    {
      useErrorBoundary: false,
      onSuccess: () => onDeleteSuccess(),
      onError: () => onDeleteError()
    },
  )
}

export const useAddressBookSearch = (
  { centreCode, searchFilterValue, pageLimit, pageOffset, sortColumn, sortType },
  config,
) => {
  const authedFetch = useAuthedFetch()
  const orderBy = formatOrderBy(sortColumn, sortType)

  const query = useQuery(
    [
      'addresses-filtered',
      centreCode,
      searchFilterValue,
      pageLimit,
      pageOffset,
      sortColumn,
      sortType
    ],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'GET',
          `${BACKEND_ENDPOINT}/client-centers/${centreCode}/client-addresses?filter_description=[contains]${searchFilterValue}&page_limit=${pageLimit}&page_offsets=${pageOffset}${orderBy}`,
          {
            parseJSON: true,
            signal,
          },
        ),
      ),
    {
      select: ({ data }) => transformAddressBookResponse(data),
      enabled: Boolean(centreCode) && Boolean(searchFilterValue),
      useErrorBoundary: false,
      ...config,
    },
  )

  return query
}

export const formatOrderBy = (sortColumn, sortType) => {
  if (!sortColumn || !sortType) return ''
  else {
    let type = sortType === 'desc' ? '-' : '+'
    let column
    if (sortColumn === 'name-company') column = 'name'
    else if(sortColumn === 'contact-client-code') column = 'contact_client_code'
    else if (sortColumn === 'address') column = 'address'
    else column = sortColumn
    return `&ordering=${encodeURIComponent(type)}${column}`
  }
}

export const formatEditAddressToBodyData = addressData => {
  const { data, rawData } = addressData
  const formattedData = {
    name: data.name,
    address: data.address,
    address_2: data.city,
    postal_code: data.postalCode.trim(),
    country_code: data.country,
    email: data.email,
    phone: data.phonePrefix + data.phone,
    comments: data.comments,
    contact_client_code: data?.contactClientCode,
    state_or_province: data.province
  }
  return {
    ...rawData,
    ...formattedData,
  }
}

export const useEditAddressMutation = (
  setServerError,
  showSuccessMsg,
  updateSavedAddress,
) => {
  const authedFetch = useAuthedFetch()
  return useMutation(
    ({ addressData }) =>
      withFetchCancel(signal =>
        authedFetch(
          'PUT',
          `${BACKEND_ENDPOINT}/client-centers/${addressData.client_center_code}/client-addresses/${addressData.client_address_code}`,
          {
            signal,
            data: addressData,
          },
        ),
      ),
    {
      useErrorBoundary: false,
      onError: error => {
        const errorTextKey =
          error.error_code === 500 ? ['error:network'] : ['error:general']
        setServerError(errorTextKey)
      },
      onSuccess: () => showSuccessMsg(),
      onSettled: (_data, _error, variables, _context) => {
        const { addressData } = variables
        updateSavedAddress(transformAddress(addressData))

        QueryClient.invalidateQueries([
          'addresses',
          addressData.client_center_code,
          addressData.address_type_code,
        ])
      },
    },
  )
}

export const useEditAddressAgendaMutation = (onSuccess, onError) => {
  const authedFetch = useAuthedFetch()

  return useMutation(
    ({ addressData, clientCenterCode, clientAddressCode }) =>
      withFetchCancel(signal =>
        authedFetch(
          'PUT',
          `${BACKEND_ENDPOINT}/client-centers/${clientCenterCode}/client-addresses/${clientAddressCode}`,
          {
            data: {
              client_center_code: clientCenterCode,
              client_code: clientCenterCode,
              name: addressData.address?.name ?? '',
              address: addressData.address?.address,
              address_2: addressData.address?.city ?? '',
              state_or_province: addressData.address?.province ?? '',
              postal_code:
                addressData.address?.postalCode?.postalCode ??
                addressData.address?.postalCode ??
                '',
              country_code: addressData.address?.country.code ?? '',
              email: addressData.address?.email ?? '',
              phone: addressData.address?.phone ?? '',
              is_active: true,
              contact_client_code:
                addressData?.address?.contactClientCode ?? '',
              comments: addressData?.comments ?? '',
            },
          },
        ),
      ),
    {
      useErrorBoundary: false,
      onError: error => {
        const errorTextKey =
          error.error_code === 500 ? ['error:network'] : ['error:general']
        onError(errorTextKey)
      },
      onSuccess: () => onSuccess(),
    },
  )
}

export const useAddAddressMutation = (onSuccess, onError) => {
  const authedFetch = useAuthedFetch()

  return useMutation(
    ({ addressData, clientCenterCode }) =>
      withFetchCancel(signal =>
        authedFetch(
          'POST',
          `${BACKEND_ENDPOINT}/client-centers/${clientCenterCode}/client-addresses`,
          {
            data: {
              address_type_code: 'ORIGIN',
              client_center_code: clientCenterCode,
              client_code: clientCenterCode,
              name: addressData.address?.name ?? '',
              address: addressData.address?.address,
              address_2: addressData.address?.city ?? '',
              state_or_province: addressData.address?.province ?? '',
              postal_code:
                addressData.address?.postalCode?.postalCode ??
                addressData.address?.postalCode ??
                '',
              country_code: addressData.address?.country.code ?? '',
              email: addressData.address?.email ?? '',
              phone: addressData.address?.phone ?? '',
              is_active: true,
              contact_client_code:
                addressData?.address?.contactClientCode ?? '',
              comments: addressData?.comments ?? '',
            },
          },
        ),
      ),
    {
      useErrorBoundary: false,
      onError: error => {
        const errorTextKey =
          error.error_code === 500 ? ['error:network'] : ['error:general']
        onError(errorTextKey)
      },
      onSuccess: () => onSuccess(),
    },
  )
}










export const useAddressBookTracking = ({
  filters,
  config = {},
  allowCancelled,
}) => {

  const queryClient = useQueryClient();
  const authedFetch = useAuthedFetch()

  const refetchRef = useRef()

  const queryKey = ["ADDRESSBOOK_TRACKING_KEY"]
  const {clientCode, centres, webUserEmail} = useUserContext()
  const identifier = clientCode ?`client_code=${clientCode}` : centres ? `client_center_code=${centres[0]?.id}` : `user_name=${webUserEmail}`
  const filt = `page_limit=${filters?.rowsPerPage}&page_offset=${filters?.page}`

  const query = useQuery(
    queryKey,
    () => {
      if (clientCode) {
        const fetchPromise = withFetchCancel(signal =>
          authedFetch(
            'GET',
            `${BACKEND_ENDPOINT}/client-centers/v1.0/client-addresses/jobs?${identifier}&${filt}`,
            {
              parseJSON: true,
              signal,
            },
          ),
        )
        refetchRef.current = () => fetchPromise
        return fetchPromise
      } else {
        return []
      }
    },
    {
      select: ({ data }) => data,
      ...config,
    },
  )

  const setData = (newData) => {
    queryClient.setQueryData(queryKey, (oldData) => {
      return {
        ...oldData,
        ...newData,
      };
    });
  };

  const manualRefetch = useCallback(() => {
    if (refetchRef.current) {
      refetchRef.current()
    }
  }, [])

  return { ...query, manualRefetch, setData }
}

export const useAddressBookTrackingProgress = ({
  filters,
  config = {},
  allowCancelled,
}) => {
  const authedFetch = useAuthedFetch()

  const refetchRef = useRef()

  const queryKey = ["ADDRESSBOOK_TRACKING_PROGRESS_KEY", filters]
  const {clientCode, centres, webUserEmail} = useUserContext()
  const identifier = clientCode ?`client_code=${clientCode}` : centres ? `client_center_code=${centres[0]?.id}` : `user_name=${webUserEmail}`
  const filt = `page_limit=${filters?.rowsPerPage}&page_offset=${filters?.page}`

  const query = useQuery(
    queryKey,
    () => {
      if (clientCode) {
        const fetchPromise = withFetchCancel(signal =>
          authedFetch(
            'GET',
            `${BACKEND_ENDPOINT}/client-centers/v1.0/client-addresses/jobs/progress?${identifier}&${filt}`,
            {
              parseJSON: true,
              signal,
            },
          ),
        )
        refetchRef.current = () => fetchPromise
        return fetchPromise
      } else {
        return []
      }
    },
    {
      select: ({ data }) => data,
      ...config,
    },
  )

  const manualRefetch = useCallback(() => {
    if (refetchRef.current) {
      refetchRef.current()
    }
  }, [])

  return { ...query, manualRefetch }
}

export const useUploadFiles = clientId => {
  const authedFetch = useAuthedFetch()
  const encodedClientId = encodeURIComponent(clientId)

  return useMutation(
    async file => {
      const response = await withFetchCancel(signal =>
        authedFetch(
          'POST',
          `${BACKEND_ENDPOINT}/v1.0/${encodedClientId}/client-addresses-documents/rpc-upload-file`,
          {
            parseJSON: true,
            signal,
            data: file,
          },
        ),
      )

      return response.data
    },
    {
      onSuccess: (data, variables) => {},
      useErrorBoundary: false,
    },
  )
}



export const useManifestFiles = (onSuccess, onError) => {
  const authedFetch = useAuthedFetch()
  const {webUserEmail, clientCode} = useUserContext()

  return useMutation(
    async ({ file, clientCenterCode }) => {
      const response = await withFetchCancel(signal =>
        authedFetch(
          'POST',
          `${BACKEND_ENDPOINT}/client-centers/v1.0/client-addresses/load-file`,
          {
            parseJSON: false,
            signal,
            data: {
              user_name: webUserEmail ?? '',
              resource_provider: file?.resource_provider,
              resource_id: file?.resource_id,
              file_name: file?.fileBodyPOST?.file_name,
              file_extension: file?.fileBodyPOST?.file_extension,
              file_size: file?.fileBodyPOST?.file_size,
              client_code: clientCode,
              client_center_code: clientCenterCode
            },
          },
        ),
      )

      return response.data
    },
    {
      onSuccess: () => onSuccess(),
      onError: () => onError(),
      useErrorBoundary: false,
    },
  )
}